import { EmployeeInformationBase } from "../json-schemas/base/employee_information"
import { I18nService } from "../utils/i18n.service"
import { InterventionPlace, offeredServiceI18nInterventionPlace } from "./offered-services.service"
import EmployeeInformationSchema from "../../../../../../json_schemas/base/employee_information.json"
import { SupplierBase } from '../json-schemas/base/supplier'
import { EmployeeFieldStandardBase } from "../json-schemas/base/employee_field_standard"
import { MarkerColor } from "../../components/shared/leaflet.component"
import i18nScopedData from './employee-informations.service.yml'
import employeeInformationSchema from "../../../../../../json_schemas/base/employee_information.json"

export type EmployeeType = EmployeeInformationBase['employee_type'];
export type EmployeeInformationState = EmployeeInformationBase['state'];
export type ContractType = EmployeeInformationBase['contract_type'];
export type InterventionSectorReference = EmployeeInformationBase['intervention_sector_reference'];
export type UnsupportedPets = EmployeeInformationBase['unsupported_pets'][0];

export const employeeInformationHonorifics = employeeInformationSchema.properties.honorific.enum as EmployeeInformationBase['honorific'][]

export const employeeInformationEmployeeTypes = EmployeeInformationSchema.properties.employee_type.enum as EmployeeType[]
const i18nScoped = new I18nService(i18nScopedData)

export function employeeInformationI18nInterventionSectorReference (reference: InterventionSectorReference, distanceRange: number) {
  return i18nScoped.t(`intervention_sector_references.${reference}`, { km: distanceRange })
}

export function employeeInformationI18nTemporaryInterventionSectorReference (reference: InterventionSectorReference, distanceRange: number) {
  return i18nScoped.t(`temporary_intervention_sector_references.${reference}`, { km: distanceRange })
}

export function employeeInformationComputedState (ei: Pick<EmployeeInformationBase, 'unexempted_independant_with_unpaid_or_without_plan?'> & { state: EmployeeInformationState | 'deleted' }) {
  if (ei.state !== 'published' || !ei['unexempted_independant_with_unpaid_or_without_plan?']) return ei.state
  return 'unexempted_independant_with_unpaid_or_without_plan' as const
}

export function employeeInformationI18nState (state: ReturnType<typeof employeeInformationComputedState>) {
  return i18nScoped.t(`state.${state}`)
}

export function employeeInformationStateCssClass (state: ReturnType<typeof employeeInformationComputedState>) {
  return { 
    draft: 'ribbon--alternate', 
    candidate: 'u-txtClr1', 
    deactivated: 'ribbon--alternate', 
    published: 'ribbon--accepted',
    unexempted_independant_with_unpaid_or_without_plan: 'ribbon--alternate',
    deleted: ''
  }[state]
}

export function employeeInformationStateMarkerColor (state: ReturnType<typeof employeeInformationComputedState>): MarkerColor {
  return ({ 
    draft: 'yellow', 
    candidate: 'grey', 
    deactivated: 'orange', 
    published: 'blue',
    unexempted_independant_with_unpaid_or_without_plan: 'yellow',
    deleted: 'red'
  } as const)[state]
}

export function employeeInformationI18nEmployeeType (employeeType: EmployeeType) {
  return i18nScoped.t(`employee_type.${employeeType}`)
}

export function employeeInformationI18nInterventionPlace (interventionPlace: InterventionPlace) {
  return offeredServiceI18nInterventionPlace(interventionPlace)
}

export function employeeInformationI18nUnsupportedPet (unsupportedPet: UnsupportedPets) {
  return i18nScoped.t(`unsupported_pets.${unsupportedPet}`)
}

export function employeeInformation18nFrenchDiplomaLevel (frenchDiplomaLevel: EmployeeInformationBase['french_diploma_level']) {
  return i18nScoped.t(`french_diploma_level.${frenchDiplomaLevel}`)
}

export function employeeInformationI18nHonorific (honorific: EmployeeInformationBase['honorific']) {
  return i18nScoped.t(`honorific.${honorific}`)
}

export function employeeInformationI18nHonorificDescription (honorific: EmployeeInformationBase['honorific']) {
  return i18nScoped.t(`honorific_description.${honorific}`)
}

export function employeeInformationVisibleEmployeeTypes (
  market: Pick<SupplierBase, 'recruitment?' | 'employee_independant_module?' | 'employee_subcontracting_module?' | 'employee_of_customer_module?'>,
  employeeInformation: Pick<EmployeeInformationBase, 'employee_type' | 'auxiliary_employee_type'>,
  employeeTypeFieldConfig?: Pick<EmployeeFieldStandardBase, 'extra'>
) {
  let employeeTypes = (market['recruitment?'] && employeeTypeFieldConfig ? employeeTypeFieldConfig.extra.one_of : employeeInformationEmployeeTypes) as EmployeeType[]

  if (!market['employee_independant_module?'] && employeeInformation.employee_type !== 'independant' && employeeInformation.auxiliary_employee_type !== 'independant') {
    employeeTypes = employeeTypes.filter(_ => _ !== 'independant')
  }

  if (!market['employee_subcontracting_module?'] && employeeInformation.employee_type !== 'subcontractor' && employeeInformation.auxiliary_employee_type !== 'subcontractor') {
    employeeTypes = employeeTypes.filter(_ => _ !== 'subcontractor')
  }

  if (!market['employee_of_customer_module?'] && employeeInformation.employee_type !== 'employee_of_customer' && employeeInformation.auxiliary_employee_type !== 'employee_of_customer') {
    employeeTypes = employeeTypes.filter(_ => _ !== 'employee_of_customer')
  }

  return employeeTypes
}

export function employeeInformationHasEmployeeTypes (
  employeeInformation: Pick<EmployeeInformationBase, 'employee_type' | 'auxiliary_employee_type'>,
  employeeType: EmployeeType
) {
  return employeeInformation.employee_type === employeeType || employeeInformation.auxiliary_employee_type === employeeType
}
